// src/admin/AdminAdPayment.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';
import api from '../utils/api';
import { API } from '../config/apiConfig';

// --- Helpers ---
const pad = (n) => String(n).padStart(2, "0");
const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};
// Period defaults to previous calendar month (1st to last day)
// This matches the monthly close process which runs on the 5th of each month
// for the previous calendar month (e.g., Feb 5 processes Jan 1-31)
const getPeriodStartDate = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11
  
  // Previous month
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  
  // First day of previous month
  return new Date(prevYear, prevMonth, 1);
};

const getPeriodEndDate = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11
  
  // Previous month
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  
  // Last day of previous month (day 0 of next month gives last day of current month)
  return new Date(prevYear, prevMonth + 1, 0);
};

export default function AdminAdPayments() {
  const [start, setStart] = useState(getPeriodStartDate());
  const [end, setEnd] = useState(getPeriodEndDate());
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [loadingApps, setLoadingApps] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [amounts, setAmounts] = useState({}); // { countryId: amount }
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [countrySearch, setCountrySearch] = useState('');

  const handleStartChange = (e) => {
    const d = new Date(e.target.value);
    if (!Number.isNaN(d.getTime())) {
      setStart(d);
      if (end < d) setEnd(d);
    }
  };

  const handleEndChange = (e) => {
    const d = new Date(e.target.value);
    if (!Number.isNaN(d.getTime()) && d >= start) {
      setEnd(d);
    }
  };

  // Fetch apps on component mount
  useEffect(() => {
    const fetchApps = async () => {
      setLoadingApps(true);
      try {
        const res = await api.get(API.adminAdApps);
        if (res.data?.success && Array.isArray(res.data.apps)) {
          setApps(res.data.apps);
        }
      } catch (err) {
        console.error('❌ Failed to fetch apps:', err);
      } finally {
        setLoadingApps(false);
      }
    };
    fetchApps();
  }, []);

  // Fetch ad providers on component mount
  useEffect(() => {
    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const res = await api.get(API.adProviders);
        if (res.data?.success && Array.isArray(res.data.providers)) {
          setProviders(res.data.providers);
        }
      } catch (err) {
        console.error('❌ Failed to fetch ad providers:', err);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, []);

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await api.get(API.countries);
        if (Array.isArray(res.data)) {
          setCountries(res.data);
        } else if (res.data?.countries && Array.isArray(res.data.countries)) {
          setCountries(res.data.countries);
        }
      } catch (err) {
        console.error('❌ Failed to fetch countries:', err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleAmountChange = (countryId, value) => {
    setAmounts(prev => ({
      ...prev,
      [countryId]: value
    }));
  };

  // Fetch existing country revenue - reusable function
  const fetchExistingRevenue = useCallback(async () => {
    // Only fetch if all required fields are selected AND countries are loaded
    if (!selectedApp || !selectedProvider || !start || !end) {
      console.log('⏸️ Skipping fetch - missing fields:', { selectedApp, selectedProvider, start, end });
      return;
    }

    // Wait for countries to load before trying to match
    if (!countries || countries.length === 0) {
      console.log('⏸️ Skipping fetch - countries not loaded yet');
      return;
    }

    try {
      const periodStartDate = toYMD(start);
      const periodEndDate = toYMD(end);
      
      console.log('🔍 Fetching existing revenue:', {
        providerId: selectedProvider,
        appId: selectedApp,
        periodStartDate,
        periodEndDate
      });

      const params = new URLSearchParams({
        providerId: selectedProvider,
        appId: selectedApp,
        periodStartDate: periodStartDate,
        periodEndDate: periodEndDate
      });

      const url = `${API.adminAdProviderCountryRevenue}?${params.toString()}`;
      console.log('📡 API URL:', url);

      const res = await api.get(url);
      console.log('✅ API Response:', res.data);

      if (res.data?.success && Array.isArray(res.data.countryRevenue)) {
        console.log('📊 Found', res.data.countryRevenue.length, 'existing records');
        
        // Populate amounts from existing data
        const existingAmounts = {};
        res.data.countryRevenue.forEach((record) => {
          console.log('📝 Processing record:', record);
          
          // Try to match by CountryId first, then by Country name
          const countryId = record.CountryId;
          if (countryId) {
            existingAmounts[countryId] = parseFloat(record.TotalAmount) || '';
            console.log(`✅ Matched by CountryId ${countryId}: ${record.TotalAmount}`);
          } else {
            // Fallback: match by country name
            const country = countries.find(
              c => {
                const countryName = c.CountryName || c.Name || c.name || c.Country;
                return countryName && countryName.toLowerCase() === (record.Country || '').toLowerCase();
              }
            );
            if (country) {
              const id = country.CountryId || country.Id || country.id;
              if (id) {
                existingAmounts[id] = parseFloat(record.TotalAmount) || '';
                console.log(`✅ Matched by Country name "${record.Country}" to ID ${id}: ${record.TotalAmount}`);
              } else {
                console.warn('⚠️ Found country but no ID:', country);
              }
            } else {
              console.warn('⚠️ Could not find country:', record.Country, 'in countries list');
            }
          }
        });
        
        console.log('💰 Final amounts to set:', existingAmounts);
        setAmounts(existingAmounts);
      } else {
        console.log('ℹ️ No country revenue data in response');
        setAmounts({});
      }
    } catch (err) {
      // Log the error for debugging
      console.error('❌ Error fetching existing revenue:', err);
      console.error('Error details:', err.response?.data || err.message);
      // If no data exists, that's fine - just clear amounts
      setAmounts({});
    }
  }, [selectedApp, selectedProvider, start, end, countries]);

  // Fetch existing country revenue when app, provider, and dates are selected
  useEffect(() => {
    fetchExistingRevenue();
  }, [fetchExistingRevenue]);

  const handleSave = async () => {
    // Validation
    if (!selectedApp || !selectedProvider) {
      setErrorMsg('Please select both App and Ad Provider');
      return;
    }

    if (!start || !end) {
      setErrorMsg('Please select both Start Date and End Date');
      return;
    }

    // Filter countries with amounts > 0
    const countryRevenueData = countries
      .filter((country) => {
        const countryId = country.CountryId || country.Id || country.id;
        const amount = amounts[countryId];
        return amount && parseFloat(amount) > 0;
      })
      .map((country) => {
        const countryId = country.CountryId || country.Id || country.id;
        const countryName = country.CountryName || country.Name || country.name || country.Country || 'Unknown';
        return {
          countryId: countryId,
          countryName: countryName,
          currency: 'USD',
          amount: parseFloat(amounts[countryId])
        };
      });

    if (countryRevenueData.length === 0) {
      setErrorMsg('Please enter at least one amount for a country');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const payload = {
        providerId: parseInt(selectedProvider, 10),
        appId: parseInt(selectedApp, 10),
        paymentDate: toYMD(new Date()), // Current date as payment date
        periodStartDate: toYMD(start),
        periodEndDate: toYMD(end),
        countryRevenue: countryRevenueData
      };

      const res = await api.post(API.adminAdProviderCountryRevenue, payload);

      if (res.data?.success) {
        setSuccessMsg(res.data.message || `Successfully saved ${countryRevenueData.length} country revenue record(s)`);
        // Refetch data after successful save to repopulate the form with saved values
        await fetchExistingRevenue();
      } else {
        setErrorMsg(res.data?.error || 'Failed to save country revenue');
      }
    } catch (err) {
      console.error('❌ Failed to save country revenue:', err);
      setErrorMsg(err.response?.data?.error || 'Failed to save country revenue');
    } finally {
      setSaving(false);
    }
  };

  // Calculate total amount from all entered amounts
  const totalAmount = useMemo(() => {
    return Object.values(amounts).reduce((sum, amount) => {
      const numAmount = parseFloat(amount) || 0;
      return sum + numAmount;
    }, 0);
  }, [amounts]);

  // Prepare table data for ProfessionalTable with search filter
  const tableData = countries
    .filter((country) => {
      if (!countrySearch.trim()) return true;
      const countryName = country.CountryName || country.Name || country.name || country.Country || '';
      return countryName.toLowerCase().includes(countrySearch.toLowerCase().trim());
    })
    .map((country) => {
      const countryId = country.CountryId || country.Id || country.id;
      const countryName = country.CountryName || country.Name || country.name || country.Country || 'Unknown';
      return {
        id: countryId,
        countryId: countryId,
        countryName: countryName,
        amount: amounts[countryId] || ''
      };
    });

  const columns = [
    {
      header: 'Country',
      accessor: 'countryName',
      render: (value) => <Typography>{value}</Typography>
    },
    {
      header: 'Currency',
      accessor: 'currency',
      style: { textAlign: 'center' },
      render: () => <Typography sx={{ fontWeight: 500 }}>$</Typography>
    },
    {
      header: 'Amount Received',
      accessor: 'amount',
      render: (value, row) => (
        <TextField
          type="number"
          value={amounts[row.countryId] || ''}
          onChange={(e) => handleAmountChange(row.countryId, e.target.value)}
          placeholder="0.00"
          size="small"
          sx={{ width: 150 }}
          inputProps={{
            step: '0.01',
            min: '0'
          }}
        />
      )
    }
  ];

  return (
    <AdminPageWrapper 
      title="Ad Provider Payments" 
      subtitle="Manage ad provider payment entries and records"
    >
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <FormControl sx={{ minWidth: 250, maxWidth: 400 }}>
            <InputLabel id="app-select-label">Select App</InputLabel>
            <Select
              labelId="app-select-label"
              label="Select App"
              value={selectedApp}
              onChange={(e) => setSelectedApp(e.target.value)}
              disabled={loadingApps}
            >
              <MenuItem value="">
                <em>Select an app</em>
              </MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.AppId} value={app.AppId}>
                  {app.AppName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 250, maxWidth: 400 }}>
            <InputLabel id="provider-select-label">Select Ad Provider</InputLabel>
            <Select
              labelId="provider-select-label"
              label="Select Ad Provider"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              disabled={loadingProviders}
            >
              <MenuItem value="">
                <em>Select an ad provider</em>
              </MenuItem>
              {providers.map((provider) => (
                <MenuItem key={provider.ProviderId} value={provider.ProviderId}>
                  {provider.ProviderName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Start Date"
            type="date"
            value={toYMD(start)}
            onChange={handleStartChange}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={toYMD(end)}
            onChange={handleEndChange}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pl: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Revenue by Country
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || !selectedApp || !selectedProvider}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
        <Box sx={{ mb: 2, pl: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Search Country"
            variant="outlined"
            size="small"
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            placeholder="Type to search countries..."
            sx={{ minWidth: 300, maxWidth: 400 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Total Amount"
            variant="outlined"
            size="small"
            value={totalAmount.toFixed(2)}
            InputProps={{
              readOnly: true,
            }}
            sx={{ minWidth: 200, maxWidth: 250 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <ProfessionalTable
          columns={columns}
          data={tableData}
          loading={loadingCountries}
          emptyMessage="No countries found"
        />
      </Box>

      {/* Snackbar for messages */}
      <Snackbar
        open={!!errorMsg || !!successMsg}
        autoHideDuration={4000}
        onClose={() => {
          setErrorMsg('');
          setSuccessMsg('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={errorMsg ? 'error' : 'success'}
          onClose={() => {
            setErrorMsg('');
            setSuccessMsg('');
          }}
          sx={{ width: '100%' }}
        >
          {errorMsg || successMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}
